/* GW Header Menu — <gw-mega-nav> + <gw-drawer-nav>
   Figma: desktop 31:2326 / 31:2628, mobile 31:2892 / 112:215 / 112:407.

   <gw-mega-nav> wraps the desktop nav list (snippets/gw-nav-desktop.liquid):
     [data-gw-mega-item]    li that owns a panel
       [data-gw-mega-toggle]  chevron button (aria-expanded, aria-controls)
       [data-gw-mega-panel]   the panel
   Opens on mouse hover (closes 150ms after the pointer leaves), on the chevron
   button, and on the first tap of the link on touch; Escape and outside
   clicks close. The theme's own Menu class (assets/theme.js:1209) never binds
   to these items — they carry none of its classes.

   <gw-drawer-nav> lives inside the theme's #NavDrawer
   (snippets/gw-nav-drawer.liquid). The theme's MobileMenu keeps opening,
   closing, overlay, scroll lock and the focus trap; this element only swaps
   panels:
     [data-gw-open="<panel id>"]  opens that panel
     [data-gw-panel]              a panel; [data-gw-parent] names its parent
     [data-gw-back]               returns to the parent panel
   Inactive panels get `hidden`. The theme's FocusTrap only tests
   offsetParent (assets/theme.js:666), so a panel hidden by transform or
   visibility alone would still be tabbable. */

const GW_MEGA_CLOSE_DELAY = 150;
const GW_PANEL_DURATION = 300;
const GW_PANEL_STATE_CLASSES = ['is-animating', 'is-offstage-right', 'is-faded'];

class GwMegaNav extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-gw-mega-item]'));
    if (!this.items.length) return;

    this.closeTimer = null;
    this.lastPointerType = 'mouse';

    this.onPointerDownCapture = (event) => {
      this.lastPointerType = event.pointerType || 'mouse';
    };
    this.onDocumentPointerDown = (event) => {
      if (!this.contains(event.target)) this.closeAll();
    };
    this.onKeydown = (event) => {
      if (event.key !== 'Escape') return;
      const openItem = this.items.find((item) => this.isOpen(item));
      if (!openItem) return;
      this.close(openItem);
      const toggle = openItem.querySelector('[data-gw-mega-toggle]');
      if (toggle && openItem.contains(document.activeElement)) toggle.focus();
    };
    this.onBlockSelect = (event) => {
      const item = event.target && event.target.closest && event.target.closest('[data-gw-mega-item]');
      if (item && this.contains(item)) this.open(item);
    };
    this.onBlockDeselect = (event) => {
      const item = event.target && event.target.closest && event.target.closest('[data-gw-mega-item]');
      if (item && this.contains(item)) this.close(item);
    };

    this.items.forEach((item) => {
      const toggle = item.querySelector('[data-gw-mega-toggle]');
      const link = item.querySelector('.gw-nav__link');

      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.open(item);
      });
      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') this.scheduleClose(item);
      });
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) this.close(item);
      });

      if (toggle) {
        toggle.addEventListener('click', (event) => {
          event.preventDefault();
          if (this.isOpen(item)) {
            this.close(item);
          } else {
            this.open(item);
          }
        });
      }

      if (link) {
        link.addEventListener('click', (event) => {
          if (this.lastPointerType !== 'mouse' && !this.isOpen(item)) {
            event.preventDefault();
            this.open(item);
          }
        });
      }
    });

    this.addEventListener('pointerdown', this.onPointerDownCapture, true);
    document.addEventListener('pointerdown', this.onDocumentPointerDown);
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  disconnectedCallback() {
    clearTimeout(this.closeTimer);
    this.removeEventListener('pointerdown', this.onPointerDownCapture, true);
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  isOpen(item) {
    return item.classList.contains('is-open');
  }

  open(item) {
    clearTimeout(this.closeTimer);
    this.items.forEach((other) => {
      if (other !== item) this.close(other);
    });
    item.classList.add('is-open');
    const toggle = item.querySelector('[data-gw-mega-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
  }

  close(item) {
    item.classList.remove('is-open');
    const toggle = item.querySelector('[data-gw-mega-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  scheduleClose(item) {
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.close(item), GW_MEGA_CLOSE_DELAY);
  }

  closeAll() {
    clearTimeout(this.closeTimer);
    this.items.forEach((item) => this.close(item));
  }
}

class GwDrawerNav extends HTMLElement {
  connectedCallback() {
    this.root = this.querySelector('[data-gw-panel="root"]');
    if (!this.root) return;

    this.active = this.root;
    this.swapTimer = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.scroller = this.closest('#NavDrawer');

    this.onClick = (event) => {
      const opener = event.target.closest('[data-gw-open]');
      if (opener && this.contains(opener)) {
        event.preventDefault();
        this.openPanel(opener);
        return;
      }
      const back = event.target.closest('[data-gw-back]');
      if (back && this.contains(back)) {
        event.preventDefault();
        this.back();
      }
    };
    this.addEventListener('click', this.onClick);

    // MobileMenu emits no open/close event; the body class is the only
    // reliable signal (assets/theme.js:1134-1149).
    this.observer = new MutationObserver(() => this.syncDrawerState());
    this.observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    this.syncDrawerState();
  }

  disconnectedCallback() {
    clearTimeout(this.swapTimer);
    if (this.observer) this.observer.disconnect();
    this.removeEventListener('click', this.onClick);
  }

  syncDrawerState() {
    const isOpen = document.body.classList.contains('js-drawer-open-left');
    document.querySelectorAll('.js-drawer-open-left-link').forEach((button) => {
      button.setAttribute('aria-expanded', String(isOpen));
    });
    if (!isOpen && this.active !== this.root) this.reset();
  }

  panelById(id) {
    return id ? this.querySelector(`#${CSS.escape(id)}`) : null;
  }

  openPanel(opener) {
    const next = this.panelById(opener.getAttribute('data-gw-open'));
    if (!next || next === this.active) return;
    next.gwOpener = opener;
    opener.setAttribute('aria-expanded', 'true');
    this.swap(this.active, next, 'forward', null);
  }

  back() {
    const from = this.active;
    if (from === this.root) return;
    const parent = this.panelById(from.getAttribute('data-gw-parent')) || this.root;
    const opener = from.gwOpener || null;
    if (opener) opener.setAttribute('aria-expanded', 'false');
    this.swap(from, parent, 'back', opener);
  }

  swap(from, to, direction, focusTarget) {
    clearTimeout(this.swapTimer);

    const finish = () => {
      from.hidden = true;
      from.classList.remove(...GW_PANEL_STATE_CLASSES);
      to.classList.remove(...GW_PANEL_STATE_CLASSES);
      this.active = to;
      if (this.scroller) this.scroller.scrollTop = 0;
      const target = focusTarget || to.querySelector('[data-gw-back]') || to.querySelector('a, button');
      if (target) target.focus({ preventScroll: true });
    };

    to.hidden = false;

    if (this.reducedMotion.matches) {
      finish();
      return;
    }

    if (direction === 'forward') {
      to.classList.add('is-offstage-right');
      void to.offsetWidth;
      to.classList.add('is-animating');
      from.classList.add('is-animating', 'is-faded');
      to.classList.remove('is-offstage-right');
    } else {
      to.classList.add('is-faded');
      void to.offsetWidth;
      to.classList.add('is-animating');
      from.classList.add('is-animating', 'is-offstage-right');
      to.classList.remove('is-faded');
    }

    this.swapTimer = setTimeout(finish, GW_PANEL_DURATION + 20);
  }

  reset() {
    clearTimeout(this.swapTimer);
    this.querySelectorAll('[data-gw-panel]').forEach((panel) => {
      panel.hidden = panel !== this.root;
      panel.classList.remove(...GW_PANEL_STATE_CLASSES);
    });
    this.querySelectorAll('[data-gw-open]').forEach((opener) => {
      opener.setAttribute('aria-expanded', 'false');
    });
    this.active = this.root;
    if (this.scroller) this.scroller.scrollTop = 0;
  }
}

if (!customElements.get('gw-mega-nav')) {
  customElements.define('gw-mega-nav', GwMegaNav);
}

if (!customElements.get('gw-drawer-nav')) {
  customElements.define('gw-drawer-nav', GwDrawerNav);
}

if (window.wetheme && window.wetheme.webcomponentRegistry) {
  window.wetheme.webcomponentRegistry.register({
    key: 'gw-header-menu',
    element: 'gw-mega-nav',
  });
}
